import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Form, Select, InputNumber, Input, Button, Space, message, Alert, Spin } from 'antd';
import { supplierAPI, customsAPI } from '../services/api.ts';
import { RectificationReason, RectificationPreview, RectifyDocumentItemPayload } from '../types.ts';
import RectificationPreviewPanel from './RectificationPreviewPanel.tsx';

const { Option } = Select;
const { TextArea } = Input;

interface DocumentItemRectifyModalProps {
  visible: boolean;
  documentId: number;
  item: { id: number; product_code: string; original_quantity_loaded: number; stock_qty_now?: number } | null;
  onCancel: () => void;
  onSuccess: () => void;
}

/**
 * Rettifica quantità dalla riga di un documento ELABORATO (porta "documento" del
 * motore lot_rectification): la merce è già in magazzino, la differenza va sulla
 * giacenza disponibile con movimento di rettifica; si sceglie se la bolletta è stata
 * rettificata dalla dogana (quantità caricata aggiornata) oppure no (differenza
 * inventariale). Spedizione e ordine fornitore vengono riallineati dal backend.
 */
const DocumentItemRectifyModal: React.FC<DocumentItemRectifyModalProps> = ({ visible, documentId, item, onCancel, onSuccess }) => {
  const [form] = Form.useForm();
  const [reasons, setReasons] = useState<RectificationReason[]>([]);
  const [preview, setPreview] = useState<RectificationPreview | null>(null);
  const [customsRectified, setCustomsRectified] = useState<boolean | null>(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (visible && item) {
      form.resetFields();
      form.setFieldsValue({ new_quantity: item.original_quantity_loaded });
      setPreview(null);
      setCustomsRectified(null);
      supplierAPI.getRectificationReasons().then(setReasons).catch(() => setReasons([]));
    }
  }, [visible, item, form]);

  const loadPreview = useCallback(async (rectified: boolean | null) => {
    if (!item) return;
    const newQty = form.getFieldValue('new_quantity');
    if (!newQty || newQty === item.original_quantity_loaded) {
      setPreview(null);
      return;
    }
    setLoadingPreview(true);
    try {
      const data = await customsAPI.previewItemRectification(documentId, item.id, {
        new_quantity: newQty,
        customs_rectified: rectified
      });
      setPreview(data);
    } catch (error: any) {
      const detail = error?.response?.data?.detail;
      message.error(typeof detail === 'string' ? detail : "Errore nel calcolo dell'anteprima");
      setPreview(null);
    } finally {
      setLoadingPreview(false);
    }
  }, [documentId, item, form]);

  const handleCustomsRectified = (value: boolean) => {
    setCustomsRectified(value);
    loadPreview(value);
  };

  const canConfirm = !!preview && preview.blocks.length === 0 && customsRectified !== null;

  const handleConfirm = async () => {
    if (!item) return;
    let values: any;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }
    if (customsRectified === null) {
      message.error('Indicare se la bolletta è stata rettificata dalla dogana');
      return;
    }
    const payload: RectifyDocumentItemPayload = {
      new_quantity: values.new_quantity,
      reason_code: values.reason_code,
      notes: values.notes?.trim() || undefined,
      customs_rectified: customsRectified
    };
    setSubmitting(true);
    try {
      const result = await customsAPI.rectifyItemQuantity(documentId, item.id, payload);
      message.success(result?.message || 'Quantità rettificata');
      onSuccess();
    } catch (error: any) {
      const detail = error?.response?.data?.detail;
      message.error(typeof detail === 'string' ? detail : 'Errore durante la rettifica');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      title={`Rettifica quantità — ${item?.product_code || ''}`}
      open={visible}
      onCancel={onCancel}
      width={760}
      destroyOnClose
      footer={[
        <Button key="cancel" onClick={onCancel}>Annulla</Button>,
        <Button key="ok" type="primary" onClick={handleConfirm} loading={submitting} disabled={!canConfirm || loadingPreview}>
          Conferma rettifica
        </Button>
      ]}
    >
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="La merce di questa riga è già in magazzino"
        description="La differenza viene applicata alla giacenza disponibile con un movimento di rettifica; riga di spedizione e ordine fornitore vengono riallineati. Scegli se la bolletta è stata rettificata dalla dogana."
      />
      <Form form={form} layout="vertical" onValuesChange={(changed) => { if ('new_quantity' in changed) { setCustomsRectified(null); loadPreview(null); } }}>
        <Space size="large" align="start" wrap>
          <Form.Item label="Quantità sulla bolletta">
            <InputNumber value={item?.original_quantity_loaded} disabled style={{ width: 160 }} />
          </Form.Item>
          <Form.Item name="new_quantity" label="Quantità reale" rules={[{ required: true, message: 'Indica la quantità' }]}>
            <InputNumber min={1} style={{ width: 160 }} />
          </Form.Item>
          <Form.Item name="reason_code" label="Motivo" rules={[{ required: true, message: 'Indica il motivo' }]} style={{ minWidth: 300 }}>
            <Select placeholder="Seleziona il motivo">
              {reasons.map((r) => <Option key={r.code} value={r.code}>{r.label}</Option>)}
            </Select>
          </Form.Item>
        </Space>
        <Form.Item name="notes" label="Note (opzionali)">
          <TextArea rows={2} className="no-uppercase-modal" />
        </Form.Item>
      </Form>

      <Spin spinning={loadingPreview}>
        <RectificationPreviewPanel
          preview={preview}
          loading={loadingPreview || submitting}
          customsRectified={customsRectified}
          onCustomsRectifiedChange={handleCustomsRectified}
        />
      </Spin>
    </Modal>
  );
};

export default DocumentItemRectifyModal;
